import { TestBattery, BatterySession } from '../types';
import { supabase, isSupabaseAvailable } from './supabaseClient';

// ── Row types ─────────────────────────────────────────────────────────────────

type BatteryRow = {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  test_ids: string[];
  is_template: boolean;
  created_by: string | null;
  created_at: string;
};

type SessionRow = {
  id: string;
  battery_id: string | null;
  battery_name: string;
  patient_id: string;
  clinician_id: string;
  test_ids: string[];
  status: 'in_progress' | 'completed' | 'abandoned';
  result_ids: string[];
  current_test_index: number;
  started_at: string;
  completed_at: string | null;
};

// ── Mappers ───────────────────────────────────────────────────────────────────

function rowToBattery(row: BatteryRow): TestBattery {
  return {
    id:          row.id,
    name:        row.name,
    description: row.description ?? undefined,
    category:    row.category ?? undefined,
    testIds:     row.test_ids,
    isTemplate:  row.is_template,
    createdBy:   row.created_by ?? undefined,
    createdAt:   row.created_at,
  };
}

function rowToSession(row: SessionRow): BatterySession {
  return {
    id:                row.id,
    batteryId:         row.battery_id ?? undefined,
    batteryName:       row.battery_name,
    patientId:         row.patient_id,
    clinicianId:       row.clinician_id,
    testIds:           row.test_ids,
    status:            row.status,
    resultIds:         row.result_ids,
    currentTestIndex:  row.current_test_index,
    startedAt:         row.started_at,
    completedAt:       row.completed_at ?? undefined,
  };
}

// ── Batteries CRUD ────────────────────────────────────────────────────────────

/** Fetch all batteries visible to the current clinician (own + templates). */
export async function getBatteries(): Promise<TestBattery[]> {
  if (!isSupabaseAvailable() || !supabase) return [];

  const { data, error } = await supabase
    .from('test_batteries')
    .select('*')
    .order('is_template', { ascending: false })
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return (data as BatteryRow[]).map(rowToBattery);
}

/** Create a new custom battery. Returns the created battery or null on error. */
export async function createBattery(input: {
  name: string;
  description?: string;
  category?: string;
  testIds: string[];
  clinicianId: string;
}): Promise<TestBattery | null> {
  if (!isSupabaseAvailable() || !supabase) return null;

  const { data, error } = await supabase
    .from('test_batteries')
    .insert({
      name:        input.name,
      description: input.description,
      category:    input.category,
      test_ids:    input.testIds,
      is_template: false,
      created_by:  input.clinicianId,
    })
    .select()
    .single();

  if (error || !data) {
    console.error('Error creating battery:', error);
    return null;
  }
  return rowToBattery(data as BatteryRow);
}

/** Update an existing custom battery. */
export async function updateBattery(
  batteryId: string,
  updates: { name?: string; description?: string; category?: string; testIds?: string[] }
): Promise<boolean> {
  if (!isSupabaseAvailable() || !supabase) return false;

  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined)        payload.name        = updates.name;
  if (updates.description !== undefined) payload.description = updates.description;
  if (updates.category !== undefined)    payload.category    = updates.category;
  if (updates.testIds !== undefined)     payload.test_ids    = updates.testIds;

  const { error } = await supabase
    .from('test_batteries')
    .update(payload)
    .eq('id', batteryId);

  return !error;
}

/** Delete a custom battery. */
export async function deleteBattery(batteryId: string): Promise<boolean> {
  if (!isSupabaseAvailable() || !supabase) return false;
  const { error } = await supabase
    .from('test_batteries')
    .delete()
    .eq('id', batteryId);
  return !error;
}

// ── Battery Sessions CRUD ─────────────────────────────────────────────────────

/** Start a new battery session for a patient. */
export async function createBatterySession(input: {
  batteryId?: string;
  batteryName: string;
  patientId: string;
  clinicianId: string;
  testIds: string[];
}): Promise<BatterySession | null> {
  if (!isSupabaseAvailable() || !supabase) return null;

  const { data, error } = await supabase
    .from('battery_sessions')
    .insert({
      battery_id:   input.batteryId,
      battery_name: input.batteryName,
      patient_id:   input.patientId,
      clinician_id: input.clinicianId,
      test_ids:     input.testIds,
    })
    .select()
    .single();

  if (error || !data) {
    console.error('Error creating battery session:', error);
    return null;
  }
  return rowToSession(data as SessionRow);
}

/** Fetch a single battery session. */
export async function getBatterySession(sessionId: string): Promise<BatterySession | null> {
  if (!isSupabaseAvailable() || !supabase) return null;

  const { data, error } = await supabase
    .from('battery_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();

  if (error || !data) return null;
  return rowToSession(data as SessionRow);
}

/** Fetch all sessions for a patient (most recent first). */
export async function getSessionsByPatient(patientId: string): Promise<BatterySession[]> {
  if (!isSupabaseAvailable() || !supabase) return [];

  const { data, error } = await supabase
    .from('battery_sessions')
    .select('*')
    .eq('patient_id', patientId)
    .order('started_at', { ascending: false });

  if (error || !data) return [];
  return (data as SessionRow[]).map(rowToSession);
}

/** Advance the session after completing one test. Pass the full updated resultIds array. */
export async function advanceBatterySession(
  sessionId: string,
  updatedResultIds: string[],
  nextIndex: number,
  totalTests: number,
): Promise<boolean> {
  if (!isSupabaseAvailable() || !supabase) return false;

  const isComplete = nextIndex >= totalTests;

  const { error } = await supabase
    .from('battery_sessions')
    .update({
      result_ids:          updatedResultIds,
      current_test_index:  nextIndex,
      status:              isComplete ? 'completed' : 'in_progress',
      completed_at:        isComplete ? new Date().toISOString() : null,
    })
    .eq('id', sessionId);

  if (error) console.error('Error advancing battery session:', error);
  return !error;
}

/** Mark a session as abandoned. */
export async function abandonBatterySession(sessionId: string): Promise<boolean> {
  if (!isSupabaseAvailable() || !supabase) return false;
  const { error } = await supabase
    .from('battery_sessions')
    .update({ status: 'abandoned' })
    .eq('id', sessionId);
  return !error;
}
